import React, { useEffect, useMemo, useRef, useState } from "react";
import "./heroLoopVideo.css";
import "./mainPage.css";
import AudioFloat from "./AudioFloat.js";

/** CONFIG */
const MOBILE_SRC = "https://lvltravel.s3.us-east-2.amazonaws.com/LVLMobile.mp4";
// Falls back to MOBILE_SRC if desktop not available
const DESKTOP_SRC = "https://lvltravel.s3.us-east-2.amazonaws.com/lvlDesktop.mp4";
const POSTER_SRC = ""; // optional jpg poster

// Intro timing (keep this in sync with CSS vars if you tweak)
const INTRO_MIN_MS = 1200;  // hard hold before fade starts
const INTRO_FADE_MS = 1620; // length of fade-out animation

/* -------------------------------------
   Detect mobile vs desktop
------------------------------------- */
function useIsMobile(query = "(max-width: 767px)") {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const on = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", on);
    else mql.addListener(on);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", on);
      else mql.removeListener(on);
    };
  }, [query]);

  return isMobile;
}

export default function HeroLoopVideo() {
  const isMobile = useIsMobile();
  const src = useMemo(
    () => (isMobile ? MOBILE_SRC : DESKTOP_SRC || MOBILE_SRC),
    [isMobile]
  );

  const videoRef = useRef(null);

  // Intro card visibility / fade state
  const [introVisible, setIntroVisible] = useState(true);
  const [introFading, setIntroFading] = useState(false);

  // Show video only after first frame is decoded
  const [firstFrameReady, setFirstFrameReady] = useState(false);

  /* --------- Preload hint for faster first frame --------- */
  useEffect(() => {
    if (!src) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "video";
    link.href = src;
    link.type = "video/mp4";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [src]);

  /* --------- Fixed intro timing: dwell, then fade, then remove --------- */
  useEffect(() => {
    // trigger fade after the minimum dwell
    const fadeTimer = setTimeout(() => setIntroFading(true), INTRO_MIN_MS);
    // fully unmount intro after fade completes
    const removeTimer = setTimeout(
      () => setIntroVisible(false),
      INTRO_MIN_MS + INTRO_FADE_MS
    );

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  /* --------- autoplay helper (esp. iOS Safari) --------- */
  const tryPlay = () => {
    const v = videoRef.current;
    if (!v) return;

    // Make sure inline autoplay rules are satisfied on iOS
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    v.setAttribute("x-webkit-airplay", "deny");
    v.setAttribute("disableRemotePlayback", "");

    v.play().catch(() => {
      // if autoplay is blocked, we fall back to user gesture below
    });
  };

  /* --------- load video / detect first usable frame --------- */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onLoadedMetadata = () => {
      // bump currentTime to dodge grey/blank first frame flashes
      try {
        v.currentTime = 0.001;
      } catch {}
    };

    const onLoadedData = () => {
      setFirstFrameReady(true);
    };

    const onCanPlay = () => {
      tryPlay();
    };

    const onError = () => {
      // if video fails, don't hold UI hostage
      setFirstFrameReady(true);
    };

    if (v.readyState >= 1) onLoadedMetadata();
    else v.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });

    if (v.readyState >= 2) onLoadedData();
    else v.addEventListener("loadeddata", onLoadedData, { once: true });

    if (v.readyState >= 3) onCanPlay();
    else v.addEventListener("canplay", onCanPlay, { once: true });

    v.addEventListener("error", onError, { once: true });

    // tiny nudge right after mount
    const earlyKick = setTimeout(tryPlay, 0);

    // User's first tap/click (Safari iOS fallback)
    const onFirstGesture = () => {
      tryPlay();
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true, passive: true });
    window.addEventListener("touchstart", onFirstGesture, { once: true, passive: true });
    window.addEventListener("click", onFirstGesture, { once: true });

    // If they tab away and back (Safari sometimes pauses)
    const onVis = () => {
      if (document.visibilityState === "visible") {
        tryPlay();
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("error", onError);

      clearTimeout(earlyKick);

      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("click", onFirstGesture);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [src]);

  return (
    <>
      {introVisible && (
        <div className={`intro-screen ${introFading ? "is-fading" : ""}`}>
          <div className="intro-block">
            <h1
              className="intro-title"
              data-text="Visions of a World, Through My Lens"
            >
              Visions of a World, Through My Lens
            </h1>

            <span className="intro-signature">— signed, Lorran</span>
          </div>
        </div>
      )}

      <div className={`hero-video-wrapper ${firstFrameReady ? "is-ready" : ""}`}>
        <video
          ref={videoRef}
          className="hero-media"
          src={src}
          poster={POSTER_SRC || undefined}
          autoPlay
          muted
          playsInline
          loop
          preload="metadata"
          controls={false}
          controlsList="nodownload noremoteplayback"
        />

        <div className="hero-cinema-veil" aria-hidden="true" />
        <div className="hero-cinema-veil-top" aria-hidden="true" />
        <div className="hero-veil-bottom" aria-hidden="true" />

        <div className="hero-overlay">
          <h1
            className="hero-title"
            data-text="Visions of a World, Through My Lens"
          >
            Visions of a World, Through My Lens
          </h1>
          <p className="hero-offerings">
            Fine Art Prints • Paintings • Humanitarian Journalism
          </p>
        </div>

        <AudioFloat
          srcs={[
            {
              src: "https://lvltravel.s3.us-east-2.amazonaws.com/Music/2ndChakra.mp3",
              type: "audio/mpeg",
            },
          ]}
          startMuted={false}    // this is how you originally had it: music is part of the entrance
          startVolume={0.5}
          loop={true}
          label="Site background audio"
          startTime={0.4}
        />
      </div>
    </>
  );
}
